import React, { useRef } from "react";
import { Plus, Trash2, RotateCcw, Download, Upload, X } from "lucide-react";

const CURRENCIES = [
  { code: "INR", label: "INR — Indian Rupee ₹" },
  { code: "USD", label: "USD — US Dollar $" },
  { code: "EUR", label: "EUR — Euro €" },
  { code: "GBP", label: "GBP — British Pound £" },
  { code: "AED", label: "AED — UAE Dirham د.إ" },
  { code: "SGD", label: "SGD — Singapore Dollar S$" },
  { code: "AUD", label: "AUD — Australian Dollar A$" },
  { code: "CAD", label: "CAD — Canadian Dollar C$" },
  { code: "JPY", label: "JPY — Japanese Yen ¥" },
  { code: "CHF", label: "CHF — Swiss Franc Fr" },
];

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] font-bold uppercase tracking-[0.12em] text-gray-600 mb-1.5">
    {children}
  </label>
);

const SectionHeading = ({ children }) => (
  <div className="flex items-center gap-3 mb-5 mt-8 first:mt-0">
    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500">
      {children}
    </span>
    <div className="flex-1 h-px bg-gray-200" />
  </div>
);

const OptionalToggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none mb-4">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-3 h-3 accent-[#002FA7] cursor-pointer"
    />
    <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-600">
      {label}
    </span>
  </label>
);

export default function InvoiceForm({
  data,
  setData,
  onPrint,
  onReset,
  downloading,
  subtotal,
  gst,
  grandTotal,
  formatINR,
}) {
  const logoRef = useRef(null);
  const upiRef = useRef(null);

  const update = (key, value) =>
    setData((prev) => ({ ...prev, [key]: value }));

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => update("logo", ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpiUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => update("upiQR", ev.target.result);
    reader.readAsDataURL(file);
  };

  const addItem = () =>
    setData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { id: Date.now(), description: "", qty: 1, rate: 0 },
      ],
    }));

  const removeItem = (id) =>
    setData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));

  const updateItem = (id, field, value) =>
    setData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));

  return (
    <div className="p-5 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
        <div>
          <h1
            className="text-xl font-bold text-gray-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Invoice Builder
          </h1>
          <p className="text-[11px] text-gray-500 mt-0.5 tracking-wide">
            Fill in details · Preview updates live
          </p>
        </div>
        <button
          data-testid="btn-reset"
          onClick={onReset}
          className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 px-3 py-2 border border-gray-200 hover:border-gray-800 transition-colors duration-200"
        >
          <RotateCcw size={11} />
          Clear
        </button>
      </div>

      {/* ── YOUR DETAILS ── */}
      <SectionHeading>Your Details</SectionHeading>

      {/* Logo Upload */}
      <div className="mb-5">
        <FieldLabel>Business Logo</FieldLabel>
        {data.logo ? (
          <div className="flex items-center gap-3">
            <img
              src={data.logo}
              alt="Logo"
              className="h-12 w-auto object-contain border border-gray-200 p-1.5"
              data-testid="logo-preview"
            />
            <button
              data-testid="btn-remove-logo"
              onClick={() => update("logo", null)}
              className="text-[11px] text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors duration-200"
            >
              <X size={11} /> Remove
            </button>
          </div>
        ) : (
          <>
            <input
              ref={logoRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoUpload}
              data-testid="input-logo-file"
            />
            <button
              data-testid="btn-upload-logo"
              onClick={() => logoRef.current.click()}
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 border border-dashed border-gray-300 hover:border-gray-800 px-4 py-3 w-full justify-center transition-colors duration-200"
            >
              <Upload size={12} />
              Upload Logo
            </button>
          </>
        )}
      </div>

      <div className="mb-4">
        <FieldLabel>Business / Firm Name</FieldLabel>
        <input
          data-testid="input-business-name"
          className="swiss-input"
          placeholder="e.g. Sharma Consulting"
          value={data.businessName}
          onChange={(e) => update("businessName", e.target.value)}
        />
      </div>

      <div className="mb-4">
        <FieldLabel>Your Name &amp; Address</FieldLabel>
        <textarea
          data-testid="input-sender-name"
          className="swiss-input resize-none"
          placeholder="e.g. Ananya Sharma"
          rows={1}
          value={data.senderName}
          onChange={(e) => update("senderName", e.target.value)}
          onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
          style={{ overflow: "hidden" }}
        />
      </div>

      {/* ── BILL TO ── */}
      <SectionHeading>Bill To</SectionHeading>

      <div className="mb-4">
        <FieldLabel>Client Name &amp; Address</FieldLabel>
        <textarea
          data-testid="input-client-name"
          className="swiss-input resize-none"
          placeholder="e.g. Acme Corporation"
          rows={1}
          value={data.clientName}
          onChange={(e) => update("clientName", e.target.value)}
          onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
          style={{ overflow: "hidden" }}
        />
      </div>

      <div className="mb-4">
        <FieldLabel>Client Email</FieldLabel>
        <input
          data-testid="input-client-email"
          type="email"
          className="swiss-input"
          placeholder="client@company.com"
          value={data.clientEmail}
          onChange={(e) => update("clientEmail", e.target.value)}
        />
      </div>

      {/* ── INVOICE DETAILS ── */}
      <SectionHeading>Invoice Details</SectionHeading>

      <div className="mb-4">
        <FieldLabel>Currency</FieldLabel>
        <select
          className="swiss-input bg-transparent"
          value={data.currency}
          onChange={(e) => update("currency", e.target.value)}
          data-testid="select-currency"
        >
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <FieldLabel>Invoice Number</FieldLabel>
        <input
          data-testid="input-invoice-number"
          className="swiss-input"
          value={data.invoiceNumber}
          onChange={(e) => update("invoiceNumber", e.target.value)}
          style={{ fontFamily: "JetBrains Mono, monospace" }}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <FieldLabel>Invoice Date</FieldLabel>
          <input
            data-testid="input-invoice-date"
            type="date"
            className="swiss-input"
            value={data.invoiceDate}
            onChange={(e) => update("invoiceDate", e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>Due Date</FieldLabel>
          <input
            data-testid="input-due-date"
            type="date"
            className="swiss-input"
            value={data.dueDate}
            onChange={(e) => update("dueDate", e.target.value)}
          />
        </div>
      </div>

      {/* ── SERVICES ── */}
      <SectionHeading>Services</SectionHeading>

      {/* Column headers */}
      <div
        className="grid gap-2 mb-2"
        style={{ gridTemplateColumns: "1fr 52px 88px 72px 26px" }}
      >
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-gray-600">
          Service
        </span>
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-gray-600 text-center">
          Qty
        </span>
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-gray-600 text-right">
          Rate
        </span>
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-gray-600 text-right">
          Amt
        </span>
        <span />
      </div>

      {/* Line items */}
      <div className="space-y-2 mb-4">
        {data.items.map((item, idx) => (
          <div
            key={item.id}
            className="grid gap-2 items-center"
            style={{ gridTemplateColumns: "1fr 52px 88px 72px 26px" }}
          >
            <input
              data-testid={`input-item-desc-${idx}`}
              className="swiss-input text-[13px]"
              placeholder="Service"
              value={item.description}
              onChange={(e) => updateItem(item.id, "description", e.target.value)}
            />
            <input
              data-testid={`input-item-qty-${idx}`}
              className="no-spinner swiss-input text-[13px] text-center"
              type="number"
              min="0"
              step="0.5"
              value={item.qty}
              onChange={(e) => updateItem(item.id, "qty", e.target.value)}
            />
            <input
              data-testid={`input-item-rate-${idx}`}
              className="no-spinner swiss-input text-[13px] text-right"
              type="number"
              min="0"
              step="100"
              value={item.rate}
              onChange={(e) => updateItem(item.id, "rate", e.target.value)}
              onFocus={() => { if (parseFloat(item.rate) === 0) updateItem(item.id, "rate", ""); }}
              onBlur={(e) => { if (e.target.value === "") updateItem(item.id, "rate", 0); }}
            />
            <span
              className="text-[12px] text-gray-700 text-right pr-0.5"
              style={{ fontFamily: "JetBrains Mono, monospace" }}
              data-testid={`item-amount-${idx}`}
            >
              {formatINR(
                (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0)
              )}
            </span>
            <button
              data-testid={`btn-remove-item-${idx}`}
              onClick={() => removeItem(item.id)}
              disabled={data.items.length === 1}
              className="text-gray-300 hover:text-red-500 disabled:opacity-25 transition-colors duration-200 flex items-center justify-center"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      <button
        data-testid="btn-add-item"
        onClick={addItem}
        className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 border border-dashed border-gray-300 hover:border-gray-800 px-4 py-2.5 w-full justify-center transition-colors duration-200 mb-5"
      >
        <Plus size={11} />
        Add Line Item
      </button>

      {/* Totals summary */}
      <div className="bg-gray-50 border border-gray-100 p-4 mb-7 space-y-2.5">
        <div className="flex justify-between text-[13px]">
          <span className="text-gray-700">Subtotal</span>
          <span
            className="text-gray-900"
            style={{ fontFamily: "JetBrains Mono, monospace" }}
            data-testid="summary-subtotal"
          >
            {formatINR(subtotal)}
          </span>
        </div>

        {/* GST row with checkbox + editable rate */}
        <div className="flex justify-between items-center text-[13px]">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={data.gstEnabled}
              onChange={(e) => update("gstEnabled", e.target.checked)}
              data-testid="checkbox-gst"
              className="w-3 h-3 accent-[#002FA7] cursor-pointer"
            />
            <span className="text-gray-700">GST</span>
            <span className="flex items-center gap-0.5 text-gray-600">
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                disabled={!data.gstEnabled}
                value={data.gstRate}
                onChange={(e) => update("gstRate", e.target.value)}
                data-testid="input-gst-rate"
                className="no-spinner w-10 text-center bg-transparent border-b border-gray-300 focus:border-gray-600 outline-none disabled:opacity-40 text-gray-600"
                style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "12px" }}
              />
              <span className="text-[11px]">%</span>
            </span>
          </label>
          <span
            className={data.gstEnabled ? "text-gray-900" : "text-gray-300"}
            style={{ fontFamily: "JetBrains Mono, monospace" }}
            data-testid="summary-gst"
          >
            {formatINR(gst)}
          </span>
        </div>

        <div className="flex justify-between text-[13px] font-bold pt-2 border-t border-gray-200">
          <span className="text-gray-900">Grand Total</span>
          <span
            className="text-gray-900"
            style={{ fontFamily: "JetBrains Mono, monospace" }}
            data-testid="summary-grand-total"
          >
            {formatINR(grandTotal)}
          </span>
        </div>
      </div>

      {/* ── TERMS & NOTES ── */}
      <SectionHeading>Terms &amp; Notes</SectionHeading>

      <div className="mb-4">
        <FieldLabel>Payment Terms</FieldLabel>
        <textarea
          data-testid="input-payment-terms"
          className="swiss-input resize-y"
          rows={5}
          value={data.paymentTerms}
          onChange={(e) => update("paymentTerms", e.target.value)}
        />
      </div>

      <div className="mb-8">
        <FieldLabel>Thank You Note</FieldLabel>
        <textarea
          data-testid="input-thank-you"
          className="swiss-input resize-none"
          rows={2}
          value={data.thankYouNote}
          onChange={(e) => update("thankYouNote", e.target.value)}
        />
      </div>

      {/* ── PAYMENT INFO ── */}
      <SectionHeading>Payment Info</SectionHeading>

      {/* Bank Details */}
      <OptionalToggle
        checked={data.bankEnabled}
        onChange={(v) => update("bankEnabled", v)}
        label="Add Bank Details"
      />

      {data.bankEnabled && (
        <div className="space-y-3 mb-6 pl-5 border-l-2 border-gray-100">
          <div>
            <FieldLabel>Account Holder Name</FieldLabel>
            <input className="swiss-input" placeholder="Full name as registered" value={data.bankHolderName} onChange={(e) => update("bankHolderName", e.target.value)} />
          </div>
          <div>
            <FieldLabel>Account Number</FieldLabel>
            <input className="swiss-input" placeholder="15 or 16-digit number" value={data.bankAccountNumber} onChange={(e) => update("bankAccountNumber", e.target.value)} style={{ fontFamily: "JetBrains Mono, monospace" }} />
          </div>
          <div>
            <FieldLabel>Bank Name &amp; Branch</FieldLabel>
            <input className="swiss-input" placeholder="e.g. HDFC Bank, Koramangala" value={data.bankName} onChange={(e) => update("bankName", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel>IFSC Code</FieldLabel>
              <input className="swiss-input" placeholder="e.g. HDFC0001234" value={data.bankIFSC} onChange={(e) => update("bankIFSC", e.target.value.toUpperCase())} style={{ fontFamily: "JetBrains Mono, monospace" }} />
            </div>
            <div>
              <FieldLabel>SWIFT / BIC</FieldLabel>
              <input className="swiss-input" placeholder="For international" value={data.bankSWIFT} onChange={(e) => update("bankSWIFT", e.target.value.toUpperCase())} style={{ fontFamily: "JetBrains Mono, monospace" }} />
            </div>
          </div>
        </div>
      )}

      {/* UPI QR Code */}
      <OptionalToggle
        checked={data.upiEnabled}
        onChange={(v) => update("upiEnabled", v)}
        label="Add UPI QR Code"
      />

      {data.upiEnabled && (
        <div className="mb-6 pl-5 border-l-2 border-gray-100">
          {data.upiQR ? (
            <div className="flex items-center gap-3">
              <img src={data.upiQR} alt="UPI QR" className="w-16 h-16 object-contain border border-gray-200 p-1" />
              <button onClick={() => update("upiQR", null)} className="text-[11px] text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors duration-200">
                <X size={11} /> Remove
              </button>
            </div>
          ) : (
            <>
              <input ref={upiRef} type="file" accept="image/*" className="hidden" onChange={handleUpiUpload} />
              <button
                onClick={() => upiRef.current.click()}
                className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 border border-dashed border-gray-300 hover:border-gray-800 px-4 py-3 w-full justify-center transition-colors duration-200"
              >
                <Upload size={12} />
                Upload QR Code
              </button>
            </>
          )}
        </div>
      )}

      {/* Brand Colour */}
      <OptionalToggle
        checked={data.brandColorEnabled}
        onChange={(v) => update("brandColorEnabled", v)}
        label="Brand Colour"
      />

      {data.brandColorEnabled && (
        <div className="mb-8 pl-5 border-l-2 border-gray-100">
          <FieldLabel>Hex Code</FieldLabel>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={data.brandColor || "#002FA7"}
              onChange={(e) => update("brandColor", e.target.value)}
              className="cursor-pointer border border-gray-200 rounded"
              style={{ width: "38px", height: "38px", padding: "2px" }}
            />
            <input
              className="swiss-input"
              placeholder="#002FA7"
              value={data.brandColor}
              onChange={(e) => {
                let val = e.target.value;
                if (val && !val.startsWith("#")) val = "#" + val;
                update("brandColor", val);
              }}
              maxLength={7}
              style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase" }}
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
            Applies to the top accent bar on your invoice.
          </p>
        </div>
      )}

      {/* Download Button — sticky at bottom of form panel on mobile */}
      <div className="form-sticky-download">
        <button
          data-testid="btn-download-pdf"
          onClick={onPrint}
          disabled={downloading}
          className="w-full flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#0028a0] disabled:opacity-70 disabled:cursor-not-allowed text-white text-[11px] font-bold uppercase tracking-widest h-12 transition-colors duration-200"
        >
          <Download size={14} />
          {downloading ? "Generating…" : "Download Invoice"}
        </button>
      </div>
    </div>
  );
}
